// Bitácora de errores.
//
// La contrapartida de «ninguna acción falla en silencio»: el usuario ve su error
// en la pantalla donde ocurrió, y aquí queda el registro con el detalle técnico
// para quien tiene que arreglarlo.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, ErrorBanner, HelpBox, OkBanner, PageHeader, Spinner } from "../../components/Layout";
import { ErroresAPI, type ErrorRegistrado } from "../../api/endpoints";

function Fila({ e, onVisto, onBorrar }: {
  e: ErrorRegistrado;
  onVisto: (id: number) => void;
  onBorrar: (id: number) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div
      className={`border rounded-md mb-2 ${
        e.visto ? "border-grid bg-superficie" : "border-estado-critico/40 bg-estado-critico/5"
      }`}
    >
      <div className="flex flex-wrap items-start gap-2 px-3 py-2">
        <button
          className="text-sm text-left flex-1 min-w-0"
          onClick={() => setAbierto(!abierto)}
        >
          <span className="text-tinta-3 mr-1">{abierto ? "▾" : "▸"}</span>
          <code className="font-mono text-[11px] text-marca">{e.origen}</code>{" "}
          <span className={e.visto ? "text-tinta-2" : "text-tinta font-medium"}>{e.mensaje}</span>
        </button>
        <span className="text-[11px] text-tinta-3 whitespace-nowrap">
          {e.created_at?.replace("T", " ").slice(0, 16)}
          {e.user_email && ` · ${e.user_email}`}
        </span>
        <div className="flex gap-1">
          {!e.visto && (
            <button
              className="text-xs px-2 py-1 rounded border border-grid bg-plano hover:bg-grid text-tinta-2"
              onClick={() => onVisto(e.id)}
              title="Marcarlo como visto: deja de contar en la campana"
            >
              Visto
            </button>
          )}
          <button
            className="text-xs px-2 py-1 rounded border border-estado-critico/40 bg-estado-critico/10 text-estado-critico hover:bg-estado-critico/20"
            onClick={() => onBorrar(e.id)}
          >
            Borrar
          </button>
        </div>
      </div>
      {abierto && (
        <div className="px-3 pb-3 border-t border-grid pt-2">
          {e.detalle ? (
            <pre className="text-[11px] font-mono bg-plano border border-grid rounded p-3 overflow-x-auto whitespace-pre-wrap">
              {e.detalle}
            </pre>
          ) : (
            <p className="text-xs text-tinta-3">Sin detalle técnico.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ErroresPage() {
  const qc = useQueryClient();
  const [soloNoVistos, setSoloNoVistos] = useState(true);
  const [aviso, setAviso] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["errores", soloNoVistos],
    queryFn: () => ErroresAPI.list(soloNoVistos, 200),
  });

  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ["errores"] });
    qc.invalidateQueries({ queryKey: ["errores-pendientes"] });
  };

  const visto = useMutation({ mutationFn: ErroresAPI.marcarVisto, onSuccess: refrescar });
  const todos = useMutation({
    mutationFn: (origen?: string) => ErroresAPI.marcarTodosVistos(origen),
    onSuccess: refrescar,
  });
  const borrar = useMutation({ mutationFn: ErroresAPI.borrar, onSuccess: refrescar });
  const purgar = useMutation({
    mutationFn: () => ErroresAPI.purgar(),
    onSuccess: (r) => {
      setAviso(`Se borraron ${r.borrados} error(es) de más de ${r.dias} días.`);
      refrescar();
      setTimeout(() => setAviso(null), 5000);
    },
  });

  const fallo =
    (q.error as Error | null) ??
    (visto.error as Error | null) ??
    (todos.error as Error | null) ??
    (borrar.error as Error | null) ??
    (purgar.error as Error | null);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <PageHeader
        title="Errores"
        subtitle="Lo que ha fallado, cuándo y por qué. Ninguna acción falla en silencio."
      />

      <HelpBox title="¿Qué se registra aquí?">
        <p>
          Todo fallo operativo que el sistema captura: una descarga de ODK que no llegó, un
          caché que no se pudo guardar, una llamada al modelo que devolvió error. El usuario
          ve el aviso en la pantalla donde ocurrió; aquí queda el <b>detalle técnico</b> para
          quien tiene que arreglarlo.
        </p>
        <p>
          El <b>origen</b> (<code className="font-mono">modulo.accion</code>) es lo que
          permite reconocer un fallo repetido: cien líneas con el mismo origen son{" "}
          <b>un</b> problema, no cien.
        </p>
        <p>
          «Visto» solo lo saca del contador de la campana; el registro se conserva. Se borra
          solo lo más antiguo que los días configurados en Configuración → General.
        </p>
      </HelpBox>

      {fallo && <div className="mb-4"><ErrorBanner message={fallo.message} /></div>}
      {aviso && <div className="mb-4"><OkBanner message={aviso} /></div>}

      <Card className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="accent-marca"
              checked={soloNoVistos}
              onChange={(e) => setSoloNoVistos(e.target.checked)}
            />
            <span className="text-tinta-2">Solo los no vistos</span>
          </label>
          <span className="text-sm text-tinta-3">
            {q.data ? `${q.data.pendientes} pendiente(s) · ${q.data.total} en total` : "…"}
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              className="btn-sec text-xs"
              disabled={todos.isPending || !q.data?.pendientes}
              onClick={() => todos.mutate(undefined)}
            >
              Marcar todos vistos
            </button>
            <button
              className="btn-peligro text-xs"
              disabled={purgar.isPending}
              onClick={() => {
                if (confirm("¿Borrar los errores más antiguos que el plazo configurado?")) {
                  purgar.mutate();
                }
              }}
            >
              Purgar antiguos
            </button>
          </div>
        </div>
      </Card>

      {q.data && q.data.por_origen.length > 0 && (
        <Card className="mb-4">
          <div className="text-sm font-medium text-tinta-2 mb-2">
            Pendientes por origen
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-tinta-3 border-b border-grid">
                <tr>
                  <th className="text-left py-2 pr-3">Origen</th>
                  <th className="text-right py-2 pr-3">Veces</th>
                  <th className="text-left py-2 pr-3">Último</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {q.data.por_origen.map((o) => (
                  <tr key={o.origen} className="border-b border-grid">
                    <td className="py-2 pr-3 font-mono text-marca">{o.origen}</td>
                    <td className="py-2 pr-3 text-right">{o.veces}</td>
                    <td className="py-2 pr-3 text-tinta-3 text-xs whitespace-nowrap">
                      {o.ultimo?.replace("T", " ").slice(0, 16)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <button
                        className="text-xs px-2 py-1 rounded border border-grid bg-plano hover:bg-grid text-tinta-2 whitespace-nowrap"
                        onClick={() => todos.mutate(o.origen)}
                      >
                        Marcar vistos
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        {q.isLoading && (
          <div className="flex items-center gap-2 text-tinta-3"><Spinner /> Cargando…</div>
        )}
        {q.data?.errores.length === 0 && (
          <p className="text-sm text-tinta-3">
            {soloNoVistos
              ? "No hay errores pendientes. "
              : "La bitácora está vacía. "}
            Es la señal que se busca.
          </p>
        )}
        {q.data?.errores.map((e) => (
          <Fila
            key={e.id}
            e={e}
            onVisto={(id) => visto.mutate(id)}
            onBorrar={(id) => borrar.mutate(id)}
          />
        ))}
      </Card>
    </div>
  );
}
